import { ExternalLinkIcon } from "lucide-react";
import { ISchedule } from "@gettactic/api";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from "@cmp-src/primitives/tooltip";

// Base interface for props that are always passed to the wrapper
interface JoinButtonWrapperProps {
  onClick: (e: React.MouseEvent<any>) => void;
  className?: string;
  disabled?: boolean;
}

// Generic type for wrapper functions that can accept different prop types
type WrapperFunction<
  TProps extends JoinButtonWrapperProps = JoinButtonWrapperProps
> = (
  children: React.ReactNode | undefined,
  icon: React.ReactNode,
  props: TProps
) => React.ReactNode;

// Base props interface for the JoinButton
interface JoinButtonBaseProps {
  /**
   * The reservation's online meeting (`schedule.online_meeting`). Nothing renders without one,
   * or when its `status` is `"error"`. While the status is `"pending"` the link is still being
   * created: the control is disabled and a tooltip says the link will be available later.
   */
  onlineMeeting?: ISchedule["online_meeting"] | null;
  /**
   * Passes the "Join" label wrapped so it can slide in on hover next to the icon, for compact
   * icon buttons. When `false` the label is plain text.
   * @default false
   */
  iconOnly?: boolean;
  /** Called when the tooltip of a pending link opens, to check whether the link is ready. */
  onRefetch?: () => void;
  /**
   * Whether the viewer may join (organizer, attendee or admin). Nothing renders when `false`.
   * @default true
   */
  hasAccess?: boolean;
}

/**
 * The "Join" action of a reservation's online meeting (Teams, Meet…). It opens the meeting
 * link in a new tab, and handles the cases where there is nothing to join yet.
 *
 * It does not render a control itself: `wrapper(children, icon, props)` renders it, so the same
 * logic works as a `Button`, a plain icon button or a `DropdownMenuItem`. Spread `props`
 * (`onClick`, `disabled`, `className`) onto that control.
 */
export const JoinButton = <
  TProps extends JoinButtonWrapperProps = JoinButtonWrapperProps
>({
  onlineMeeting,
  iconOnly = false,
  onRefetch,
  wrapper,
  hasAccess = true
}: JoinButtonBaseProps & {
  wrapper: WrapperFunction<TProps>;
}) => {
  // "error" means the link could not be created: there is nothing to join
  if (!onlineMeeting || !hasAccess || onlineMeeting.status === "error") {
    return null;
  }

  const isPending = onlineMeeting.status === "pending";
  const isDisabled = isPending;

  const onClick = (e: React.MouseEvent<any>) => {
    e.stopPropagation();

    if (isPending) return;

    // Only open URL if we have one and status is not pending
    if (onlineMeeting.url && onlineMeeting.status === "success") {
      window.open(onlineMeeting.url, "_blank", "noopener,noreferrer");
    }
  };

  const buttonContent = wrapper(
    iconOnly ? (
      <span className="overflow-hidden whitespace-nowrap">
        <span className="pl-2">Join</span>
      </span>
    ) : (
      "Join"
    ),
    <JoinIcon />,
    {
      onClick,
      disabled: isDisabled,
      className: `
        group
        grid items-center gap-0
        grid-cols-[auto_0fr] hover:grid-cols-[auto_1fr]
        transition-[grid-template-columns] duration-300 ease-in-out
        overflow-hidden
        ${isDisabled ? "opacity-50 cursor-not-allowed" : ""}
      `
    } as TProps
  );

  // If pending, wrap with tooltip
  if (isPending) {
    return (
      <TooltipProvider>
        {/* The link is still being created: opening the tooltip is the moment to check again */}
        <Tooltip onOpenChange={(isOpen) => isOpen && onRefetch?.()}>
          {/* A span, not a <button>: the wrapper already renders the control, and a
              disabled control would not show the tooltip on its own */}
          <TooltipTrigger asChild>
            <span className="inline-flex">{buttonContent}</span>
          </TooltipTrigger>
          <TooltipContent>Link to join will be available later</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return buttonContent;
};

export const JoinIcon = () => {
  return <ExternalLinkIcon />;
};
